import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { RotateCcw, Send, X, Timer, Loader2, Play, Star } from 'lucide-react';
import { checkEnglishWord } from '../../../utils/englishDictionary';
import { useTranslation } from '../../../i18n';

const BASE_TILE_SIZE = 52;
const BASE_GAP = 6;
const TILES_PADDING = 16;
const ACTION_BAR_HEIGHT = 44;
const STATUS_BAR_HEIGHT = 40;
const CURRENT_WORD_HEIGHT = 36;

const normalizeWord = (w) => (w || '').toUpperCase().replace(/[^A-Z]/gi, '');

const splitPool = (letters) =>
  (letters || '').toUpperCase().replace(/[^A-Z]/gi, '').split('');

// Can `word` be assembled from the letter pool, using each tile at most once?
const canBuildFromPool = (pool, word) => {
  const w = normalizeWord(word);
  if (!w) return false;
  const counts = {};
  for (const ch of pool) counts[ch] = (counts[ch] || 0) + 1;
  for (const ch of w) {
    if (!counts[ch]) return false;
    counts[ch] -= 1;
  }
  return true;
};

const WordPoolElement = ({
  element,
  isEditor = false,
  studentView = false,
  currentState = null,
  showResult = false,
  isCorrect = null,
  onStateChange,
  onCheck,
  onRetry,
  disabled = false,
  scale = 1,
  hideCheckButton = false,
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    mode = 'time',
    letters = '',
    layout = 'wrap',
    targetWords = [],
    timeLimit = 120,
    minWordsToPass = 3,
    minWordLength = 3,
    allowRetry = false,
    starThresholds,
    tileColor = '#3d6b3d',
    actionBarColor = '#f9fafb',
    fontFamily,
    fontSize: customFontSize,
    textColor = '#ffffff',
  } = content || {};
  const isTimeMode = mode === 'time';

  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  const pool = useMemo(() => splitPool(letters), [letters]);
  const normTargets = useMemo(
    () => (targetWords || []).map(normalizeWord).filter(Boolean),
    [targetWords]
  );

  const containerRef = useRef(null);
  const [boxScale, setBoxScale] = useState(scale);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = (w, h) => {
      if (w <= 0 || h <= 0) return;
      const n = Math.max(1, pool.length);
      let cols;
      let rows;
      let naturalWidth;
      let naturalHeight;
      if (layout === 'row') {
        cols = n;
        rows = 1;
        naturalWidth = cols * BASE_TILE_SIZE + (cols - 1) * BASE_GAP + TILES_PADDING * 2;
        naturalHeight = BASE_TILE_SIZE + TILES_PADDING * 2;
      } else if (layout === 'square') {
        cols = Math.ceil(Math.sqrt(n));
        rows = Math.ceil(n / cols);
        naturalWidth = cols * BASE_TILE_SIZE + (cols - 1) * BASE_GAP + TILES_PADDING * 2;
        naturalHeight = rows * BASE_TILE_SIZE + (rows - 1) * BASE_GAP + TILES_PADDING * 2;
      } else if (layout === 'circle') {
        // Tile centers on a ring; outer diameter = 2r + tileSize
        const ringRadius = (n * (BASE_TILE_SIZE + BASE_GAP)) / (2 * Math.PI);
        const diameter = 2 * ringRadius + BASE_TILE_SIZE;
        naturalWidth = diameter + TILES_PADDING * 2;
        naturalHeight = diameter + TILES_PADDING * 2;
      } else {
        // 'wrap'
        cols = Math.max(4, Math.ceil(n / 2));
        rows = Math.ceil(n / cols);
        naturalWidth = cols * BASE_TILE_SIZE + (cols - 1) * BASE_GAP + TILES_PADDING * 2;
        naturalHeight = rows * BASE_TILE_SIZE + (rows - 1) * BASE_GAP + TILES_PADDING * 2;
      }
      naturalHeight += STATUS_BAR_HEIGHT + CURRENT_WORD_HEIGHT + ACTION_BAR_HEIGHT;
      const next = Math.min(w / naturalWidth, h / naturalHeight) * scale;
      setBoxScale(Math.max(0.3, next));
    };
    update(node.clientWidth, node.clientHeight);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) update(e.contentRect.width, e.contentRect.height);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [scale, pool.length, layout]);

  const tileSize = Math.max(22, Math.round(BASE_TILE_SIZE * boxScale));
  const tileFontSize = Math.max(11, Math.round((customFontSize || 22) * boxScale));
  const tileGap = Math.max(2, Math.round(BASE_GAP * boxScale));
  const tileRadius = Math.max(4, Math.round(10 * boxScale));

  // path = indices into `pool` used in the current word
  const [path, setPath] = useState(currentState?.path || []);
  const [foundWords, setFoundWords] = useState(currentState?.foundWords || []);
  const [startedAt, setStartedAt] = useState(currentState?.startedAt || 0);
  const [timerRemaining, setTimerRemaining] = useState(timeLimit);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!currentState) return;
    setPath(currentState.path || []);
    setFoundWords(currentState.foundWords || []);
    // Don't overwrite a running timer just because the parent didn't echo
    // `startedAt` back — many parent handlers only persist `path`/`foundWords`.
    if (typeof currentState.startedAt === 'number' && currentState.startedAt > 0) {
      setStartedAt(currentState.startedAt);
    }
  }, [currentState]);

  useEffect(() => {
    if (isEditor || !isTimeMode || !startedAt || showResult) return;
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setTimerRemaining(Math.max(0, timeLimit - elapsed));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [isEditor, isTimeMode, startedAt, showResult, timeLimit]);

  const expiredRef = useRef(false);
  useEffect(() => {
    expiredRef.current = false;
  }, [startedAt]);
  useEffect(() => {
    if (isEditor || !isTimeMode || !startedAt || showResult) return;
    if (timerRemaining > 0) return;
    if (expiredRef.current) return;
    expiredRef.current = true;
    onCheck?.({ foundWords, totalRequired: normTargets.length, mode: 'time' });
  }, [isEditor, isTimeMode, startedAt, showResult, timerRemaining, onCheck, foundWords, normTargets.length]);

  const emit = (next) => onStateChange?.(next);

  const handleStart = () => {
    if (!isTimeMode || startedAt || isEditor || showResult) return;
    const ts = Date.now();
    setStartedAt(ts);
    emit({ path: [], foundWords, startedAt: ts });
  };

  const handleTileClick = useCallback((idx) => {
    if (disabled || showResult || isEditor) return;
    // In time mode the explicit Start button is required first.
    if (isTimeMode && !startedAt) return;
    setErrorMsg('');
    const started = startedAt;
    if (path.includes(idx)) {
      const next = path.filter((i) => i !== idx);
      setPath(next);
      emit({ path: next, foundWords, startedAt: started });
      return;
    }
    const next = [...path, idx];
    setPath(next);
    emit({ path: next, foundWords, startedAt: started });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, showResult, isEditor, path, foundWords, startedAt, isTimeMode]);

  const currentWord = path.map((i) => pool[i] || '').join('').toUpperCase();

  const handleClearPath = () => {
    if (disabled || showResult || isEditor) return;
    setPath([]);
    setErrorMsg('');
    emit({ path: [], foundWords, startedAt });
  };

  const handleSubmitWord = async () => {
    if (disabled || showResult || isEditor) return;
    if (!currentWord || currentWord.length < Math.max(2, minWordLength)) {
      setErrorMsg(t('games.wordPool.minLetters', { count: Math.max(2, minWordLength) }));
      return;
    }
    if (foundWords.includes(currentWord)) {
      setErrorMsg(t('games.wordPool.alreadyFound'));
      return;
    }

    let accept = false;
    if (isTimeMode) {
      setSubmitting(true);
      accept = await checkEnglishWord(currentWord);
      setSubmitting(false);
      if (!accept) {
        setErrorMsg(t('games.wordPool.notInDictionary'));
        return;
      }
    } else {
      // targets mode: only target list counts as a find
      if (normTargets.includes(currentWord)) {
        accept = true;
      } else {
        setErrorMsg(t('games.wordPool.notInList'));
        return;
      }
    }

    const nextFound = [...foundWords, currentWord];
    setFoundWords(nextFound);
    setPath([]);
    setErrorMsg('');
    emit({ path: [], foundWords: nextFound, startedAt });
  };

  const handleCheck = () => {
    onCheck?.({ foundWords, totalRequired: normTargets.length, mode });
  };

  const handleRetry = () => {
    setPath([]);
    setFoundWords([]);
    setStartedAt(0);
    setTimerRemaining(timeLimit);
    setErrorMsg('');
    onRetry?.();
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Renders the letter pool according to the selected `layout`. `renderTile`
  // returns the tile content without a React key (the wrapper sets one).
  const renderTiles = (renderTile) => {
    if (pool.length === 0) return null;
    if (layout === 'circle') {
      const n = pool.length;
      const ringRadius = (n * (tileSize + tileGap)) / (2 * Math.PI);
      const diameter = 2 * ringRadius + tileSize + 4;
      return (
        <div className="relative" style={{ width: diameter, height: diameter }}>
          {pool.map((ch, i) => {
            const angle = (2 * Math.PI * i) / n - Math.PI / 2;
            const cx = (diameter - tileSize) / 2;
            const cy = (diameter - tileSize) / 2;
            const x = cx + ringRadius * Math.cos(angle);
            const y = cy + ringRadius * Math.sin(angle);
            return (
              <div key={i} className="absolute" style={{ left: x, top: y }}>
                {renderTile(ch, i)}
              </div>
            );
          })}
        </div>
      );
    }
    let containerClass;
    let containerStyle;
    if (layout === 'row') {
      containerClass = 'flex items-center justify-center';
      containerStyle = { gap: `${tileGap}px`, flexWrap: 'nowrap' };
    } else if (layout === 'square') {
      const cols = Math.ceil(Math.sqrt(pool.length));
      containerClass = 'grid';
      containerStyle = {
        gridTemplateColumns: `repeat(${cols}, ${tileSize}px)`,
        gap: `${tileGap}px`,
      };
    } else {
      containerClass = 'flex flex-wrap items-center justify-center';
      containerStyle = { gap: `${tileGap}px` };
    }
    return (
      <div className={containerClass} style={containerStyle}>
        {pool.map((ch, i) => (
          <React.Fragment key={i}>{renderTile(ch, i)}</React.Fragment>
        ))}
      </div>
    );
  };

  // ---- EDITOR PREVIEW ----
  if (isEditor) {
    return (
      <div
        ref={containerRef}
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-2 text-xs text-gray-600 overflow-hidden">
          {isTimeMode ? (
            <>
              <Timer size={12} className="text-indigo-500 flex-shrink-0" />
              <span className="font-medium text-gray-700 flex-shrink-0">
                {t('games.wordPool.timeLimit', { time: formatTime(timeLimit) })}
              </span>
              {minWordsToPass > 0 && (
                <span className="text-gray-500 truncate">
                  · {t('games.wordPool.minWords', { count: minWordsToPass })} {minWordsToPass === 1 ? t('games.wordPool.wordSingular') : t('games.wordPool.wordPlural')}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="font-medium text-gray-700 flex-shrink-0">
                {t('games.wordPool.findWordsCount', { count: normTargets.length })}
              </span>
              <div className="flex flex-wrap gap-1 min-w-0">
                {normTargets.slice(0, 6).map((w) => (
                  <span key={w} className="px-2 py-0.5 rounded-full bg-white border border-gray-200">
                    {w}
                  </span>
                ))}
                {normTargets.length > 6 && <span>…</span>}
              </div>
            </>
          )}
        </div>

        <div className="flex-1 min-h-0 p-3 flex items-center justify-center overflow-hidden">
          {pool.length > 0 ? (
            renderTiles((ch) => (
              <div
                className="flex items-center justify-center font-bold"
                style={{
                  width: `${tileSize}px`,
                  height: `${tileSize}px`,
                  fontSize: `${tileFontSize}px`,
                  borderRadius: `${tileRadius}px`,
                  backgroundColor: tileColor,
                  color: textColor,
                  ...(fontFamily ? { fontFamily } : {}),
                }}
              >
                {ch}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400">{t('games.wordPool.fillLettersInSettings')}</p>
          )}
        </div>

      </div>
    );
  }

  // ---- INTERACTIVE / CLASSROOM ----
  const hideChrome = true;
  const foundTargets = normTargets.filter((w) => foundWords.includes(w));
  const bonusWords = foundWords.filter((w) => !normTargets.includes(w));

  // Mirror VirtualClassroom.handleBoggleCheck: pick the highest tier the
  // student's valid word count meets. Kept here so showResult can render stars
  // without threading extra state through the classroom sync layer.
  const validFoundCount = isTimeMode ? foundWords.length : foundTargets.length;
  const tierThresholds = Array.isArray(starThresholds) && starThresholds.length === 5
    ? starThresholds
    : [1, 2, 5, 8, 11];
  let earnedStars = 0;
  for (let tier = 5; tier >= 1; tier--) {
    if (validFoundCount >= tierThresholds[tier - 1]) { earnedStars = tier; break; }
  }

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex flex-col rounded-xl overflow-hidden ${
        hideChrome ? '' : 'border-2 border-transparent shadow-sm'
      }`}
      style={{ backgroundColor: 'transparent' }}
    >
      {isTimeMode ? (
        <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50 flex items-center gap-2 text-xs overflow-hidden">
          <Timer
            size={14}
            className={timerRemaining <= 10 ? 'text-red-500' : 'text-indigo-500'}
          />
          <span
            className={`font-mono font-bold flex-shrink-0 ${
              timerRemaining <= 10 ? 'text-red-600' : 'text-gray-800'
            }`}
          >
            {formatTime(startedAt ? timerRemaining : timeLimit)}
          </span>
          <span className="text-gray-700 font-medium flex-shrink-0">
            · {t('games.wordPool.found', { count: foundWords.length })}
          </span>
          {minWordsToPass > 0 && (
            <span className="text-gray-500 flex-shrink-0">{t('games.wordPool.minWords', { count: minWordsToPass })}</span>
          )}
          <div className="flex flex-wrap gap-1 min-w-0 ml-2">
            {foundWords.map((w) => (
              <span
                key={w}
                className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium"
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50 flex items-center gap-2 text-xs overflow-hidden">
            <span className="text-gray-700 font-medium flex-shrink-0">
              {t('games.wordPool.targets', { done: foundTargets.length, total: normTargets.length })}
            </span>
            <div className="flex flex-wrap gap-1 min-w-0">
              {normTargets.map((w) => {
                const found = foundWords.includes(w);
                return (
                  <span
                    key={w}
                    className={`px-2 py-0.5 rounded-full font-medium ${
                      found
                        ? 'bg-green-100 text-green-700'
                        : 'bg-white border border-gray-200 text-gray-400'
                    }`}
                  >
                    {found ? w : '·'.repeat(w.length)}
                  </span>
                );
              })}
            </div>
          </div>
          {bonusWords.length > 0 && (
            <div className="px-3 py-1 border-b border-gray-100 bg-amber-50 flex items-center gap-2 text-xs overflow-hidden">
              <span className="text-amber-700 font-medium flex-shrink-0">
                {t('games.wordPool.bonus', { count: bonusWords.length })}
              </span>
              <div className="flex flex-wrap gap-1 min-w-0">
                {bonusWords.map((w) => (
                  <span
                    key={w}
                    className="px-2 py-0.5 rounded-full bg-white text-amber-700 border border-amber-200 font-medium"
                  >
                    {w}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Letter pool */}
      <div className="flex-1 min-h-0 p-3 flex items-center justify-center overflow-hidden relative">
        {pool.length > 0 ? (
          <>
            <div
              className={
                isTimeMode && !startedAt && !showResult
                  ? 'blur-md opacity-30 pointer-events-none transition'
                  : 'transition'
              }
            >
              {renderTiles((ch, i) => {
                const inPath = path.includes(i);
                return (
                  <button
                    type="button"
                    onClick={() => handleTileClick(i)}
                    disabled={disabled || showResult}
                    className={`
                      flex items-center justify-center font-bold select-none
                      transition-all duration-150
                      ${!disabled && !showResult ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
                      ${inPath ? 'ring-4 ring-yellow-300 ring-offset-1 scale-105' : ''}
                    `}
                    style={{
                      width: `${tileSize}px`,
                      height: `${tileSize}px`,
                      fontSize: `${tileFontSize}px`,
                      borderRadius: `${tileRadius}px`,
                      backgroundColor: tileColor,
                      color: textColor,
                      opacity: inPath ? 0.55 : 1,
                      ...(fontFamily ? { fontFamily } : {}),
                    }}
                  >
                    {ch}
                  </button>
                );
              })}
            </div>
            {isTimeMode && !startedAt && !showResult && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={handleStart}
                  className="flex items-center gap-2 px-6 py-3 text-white text-base font-medium rounded-xl shadow-lg transition"
                  style={{ backgroundColor: '#2A4D25' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e3a1a'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2A4D25'; }}
                >
                  <Play size={20} />
                  {t('games.wordPool.timedStart')}
                </button>
                <p className="text-xs text-gray-600">
                  {t('games.wordPool.lettersAppearOnStart', { time: formatTime(timeLimit) })}
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-400">{t('games.wordPool.lettersNotFilled')}</p>
        )}
      </div>

      {/* Current selection bar */}
      {!showResult && (
        <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 flex items-center gap-2 text-sm">
          <span className="text-gray-500 text-xs flex-shrink-0">{t('games.wordPool.wordLabel')}</span>
          <span className="font-bold tracking-wider text-gray-800 flex-1 truncate">
            {currentWord || '—'}
          </span>
          {errorMsg && (
            <span className="text-xs text-red-600 truncate">{errorMsg}</span>
          )}
          {path.length > 0 && (
            <button
              onClick={handleClearPath}
              className="p-1 hover:bg-gray-200 rounded transition"
              title={t('games.wordPool.clear')}
            >
              <X size={14} className="text-gray-500" />
            </button>
          )}
          <button
            onClick={handleSubmitWord}
            disabled={path.length < Math.max(2, minWordLength) || submitting}
            className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition ${
              path.length >= Math.max(2, minWordLength) && !submitting
                ? 'text-white'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            style={
              path.length >= Math.max(2, minWordLength) && !submitting
                ? { backgroundColor: '#2A4D25' }
                : undefined
            }
            onMouseEnter={(e) => {
              if (path.length >= Math.max(2, minWordLength) && !submitting) {
                e.currentTarget.style.backgroundColor = '#1e3a1a';
              }
            }}
            onMouseLeave={(e) => {
              if (path.length >= Math.max(2, minWordLength) && !submitting) {
                e.currentTarget.style.backgroundColor = '#2A4D25';
              }
            }}
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {t('games.wordPool.addLetters')}
          </button>
        </div>
      )}

      {/* Action bar */}
      <div
        className={`px-3 py-2 flex items-center justify-end gap-2 ${
          hideChrome || isActionBarTransparent ? '' : 'border-t border-gray-100'
        }`}
        style={{ backgroundColor: hideChrome ? 'transparent' : actionBarColor }}
      >
        {showResult && !isCorrect && allowRetry && (
          <button
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
          >
            <RotateCcw size={14} />
            {t('games.common.tryAgain')}
          </button>
        )}

        {!showResult && !hideCheckButton && (
          <button
            onClick={handleCheck}
            disabled={!isTimeMode && normTargets.length === 0}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${
              isTimeMode || normTargets.length > 0
                ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isTimeMode ? t('games.common.finish') : t('games.common.check')}
          </button>
        )}

        {showResult && (
          <div className="flex items-center gap-1 mr-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                size={16}
                className={i < earnedStars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 fill-gray-200'}
              />
            ))}
            <span className="ml-1 text-xs font-semibold text-gray-700">{earnedStars}/5</span>
          </div>
        )}

      </div>
    </div>
  );
};

export { canBuildFromPool };
export default WordPoolElement;
